import { MouseEvent, useCallback } from 'react';
import { Chain } from '@ankr.com/chains-list';

import { useAppSelector } from 'store/useAppSelector';
import { usePrivateChainItem } from 'domains/chains/screens/ChainPage/PrivateChainItemQuery/components/PrivateChainItem/hooks/usePrivateChainItem';
import {
  selectBlockchainBySubchainId,
  selectPublicChainById,
} from 'modules/chains/store/selectors';
import { useDialog } from 'modules/common/hooks/useDialog';
import { hasEvmSubchains } from 'modules/chains/utils/hasEvmSubchains';
import { useAuth } from 'modules/auth/hooks/useAuth';

import { useAddNetworkButtonClick } from './useAddNetworkButtonClick';

interface IUseAddNetworkButtonProps {
  chain: Chain;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onDialogClick: (event: MouseEvent<HTMLElement | HTMLButtonElement>) => void;
  hasChainSelector?: boolean;
}

export const useAddNetworkButton = ({
  chain,
  hasChainSelector,
  onClick,
  onDialogClick,
}: IUseAddNetworkButtonProps) => {
  const { isLoggedIn } = useAuth();

  const currentChainMainId = useAppSelector(state =>
    selectBlockchainBySubchainId(state, chain.id),
  )?.id;

  const publicChain =
    useAppSelector(state =>
      selectPublicChainById(state, currentChainMainId || chain.id),
    ) || chain;

  const {
    chainProtocolContext,
    chainSubType,
    chainSubTypeTab,
    chainSubTypeTabs,
    chainType,
    chainTypeTab,
    chainTypeTabs,
    group,
    groupID,
    groupTab,
    groupTabs,
    groups,
    selectGroup,
  } = usePrivateChainItem({
    chain: isLoggedIn ? chain : publicChain,
    isGroupSelectorAutoWidth: true,
  });

  const handleButtonClick = useAddNetworkButtonClick({
    chain: isLoggedIn ? chain : publicChain,
    chainType,
    chainSubType,
    group,
  });

  const { isOpened, onClose, onOpen } = useDialog();

  const openChainSelectorDialog = useCallback(
    (event: MouseEvent<HTMLElement | HTMLButtonElement>) => {
      onDialogClick(event);
      onOpen();
    },
    [onDialogClick, onOpen],
  );

  /* hiding the addNetwork button for networks not supported in MetaMask */
  const shouldHideButton =
    (!handleButtonClick && !hasChainSelector) || !hasEvmSubchains(chain);

  const onButtonClick = useCallback(
    (event: React.MouseEvent<HTMLElement | HTMLButtonElement>) => {
      if (onClick) {
        return onClick(event as MouseEvent<HTMLButtonElement>);
      }

      if (hasChainSelector) {
        return openChainSelectorDialog(event);
      }

      if (handleButtonClick) {
        return handleButtonClick(event as MouseEvent<HTMLElement>);
      }
    },
    [handleButtonClick, hasChainSelector, onClick, openChainSelectorDialog],
  );

  return {
    shouldHideButton,
    chainProtocolContext,
    onButtonClick,
    onClose,
    isOpened,
    chainSubTypeTab,
    chainSubTypeTabs,
    chainTypeTab,
    chainTypeTabs,
    groupID,
    groupTab,
    groupTabs,
    groups,
    selectGroup,
    handleButtonClick,
  };
};
